import { useFormikContext } from 'formik'
import styled from 'styled-components'
import { Grid, H, Divider, P } from '@farewill/ui'
import { screenMax } from '@farewill/ui/helpers/responsive'

import InputFloating from 'components/form/input-floating'
import CircularRadioGroup from 'components/form/circular-radio-group'
import AddressInput from 'components/form/address-input'
import axiosRequest from 'lib/axios/api-request'

const StyledSpacer = styled(Grid.Item)`
  ${screenMax.m`
    display: none;
  `}
`

const Pacemaker = ({ handleSave, values }) => {
  const {
    deathCountry,
    hasHazardousImplant,
    isHazardousImplantRemoved,
    hasExplosiveImplant,
    hasOtherHazardousImplant,
  } = values

  const title = deathCountry?.name === 'Scotland' ? 'Hazards' : 'Pacemaker'

  return (
    <>
      <Grid.Item>
        <H size="S">4.2 {title}</H>
      </Grid.Item>
      {deathCountry?.name !== 'Scotland' && (
        <>
          <Grid.Item>
            <P>Do they have a pacemaker or other hazardous implant?</P>
            <P size="S">
              Something which could be hazardous during the funeral. For
              example, a pacemaker, radioactive device, battery powered device
              or &quot;Fixion&quot; intramedullary nailing system.
            </P>
          </Grid.Item>
          <Grid.Item>
            <CircularRadioGroup
              name="hasHazardousImplant"
              options={[
                { label: 'Not asked', value: '' },
                { label: 'No', value: 'no' },
                { label: 'Yes', value: 'yes' },
                { label: "The customer doesn't know", value: 'not_sure' },
              ]}
              handleSave={handleSave}
              inline
            />
          </Grid.Item>
          {hasHazardousImplant === 'yes' && (
            <Grid.Item>
              <P>Has the implant already been removed?</P>
              <CircularRadioGroup
                name="isHazardousImplantRemoved"
                options={[
                  { label: 'No', value: 'no' },
                  { label: 'Yes', value: 'yes' },
                  { label: "The customer doesn't know", value: 'not_sure' },
                ]}
                handleSave={handleSave}
                inline
              />
            </Grid.Item>
          )}
        </>
      )}
      {deathCountry?.name === 'Scotland' && (
        <>
          <Grid.Item>
            <P>
              Does the body pose a risk to public health: for example did the
              deceased have a notifiable infectious disease or was their body
              contaminated immediately before death?
            </P>
            <CircularRadioGroup
              name="isBodyRiskToPublicHealth"
              options={[
                { label: 'Not asked', value: '' },
                { label: 'No', value: 'false' },
                { label: 'Yes', value: 'true' },
              ]}
              handleSave={handleSave}
              inline
            />
          </Grid.Item>
          <Grid.Item>
            <P>
              Is there a pacemaker or any other potentially explosive device
              currently present in or on the body?
            </P>
            <CircularRadioGroup
              name="hasExplosiveImplant"
              options={[
                { label: 'Not asked', value: '' },
                { label: 'No', value: 'false' },
                { label: 'Yes', value: 'true' },
              ]}
              handleSave={handleSave}
              inline
            />
          </Grid.Item>
          <Grid.Item>
            <P>
              Is there radioactive material or any other hazardous implant
              currently present in or on the body?
            </P>
            <CircularRadioGroup
              name="hasOtherHazardousImplant"
              options={[
                { label: 'Not asked', value: '' },
                { label: 'No', value: 'false' },
                { label: 'Yes', value: 'true' },
              ]}
              handleSave={handleSave}
              inline
            />
          </Grid.Item>
        </>
      )}
      {((hasHazardousImplant === 'yes' &&
        (isHazardousImplantRemoved === 'no' ||
          isHazardousImplantRemoved === 'not_sure')) ||
        [hasExplosiveImplant, hasOtherHazardousImplant].includes('true')) && (
        <Grid.Item>
          <InputFloating
            name="hazardousImplantNotes"
            label={
              deathCountry?.name === 'Scotland'
                ? 'Description of implant and/or radioactive material'
                : 'Details of the device and its location'
            }
            component="textarea"
            rows={4}
            handleSave={handleSave}
          />
        </Grid.Item>
      )}
    </>
  )
}

const DoctorsAndMedical = ({ handleSave }) => {
  const { values } = useFormikContext()

  const updateAddress = (id, attributes) => {
    return axiosRequest({
      url: `/api/addresses/${id}`,
      method: 'PATCH',
      data: {
        data: {
          type: 'addresses',
          id,
          attributes,
        },
      },
    })
  }

  return (
    <Grid>
      <Grid.Item>
        <H size="M">4. Doctors and medical</H>
      </Grid.Item>
      <Grid.Item>
        <H size="S">4.1 The doctor who attended the deceased person</H>
      </Grid.Item>
      <Grid.Item>
        <Grid>
          <Grid.Item>
            <Grid>
              <Grid.Item span={6}>
                <InputFloating
                  name="attendingDoctorName"
                  label="Full name"
                  handleSave={handleSave}
                />
              </Grid.Item>
              <Grid.Item>
                <AddressInput
                  label="Address"
                  name="attendingDoctorAddress"
                  addressValues={values.attendingDoctorAddress}
                  onAddressChanged={(attributes) => {
                    updateAddress(values.attendingDoctorAddress.id, attributes)
                  }}
                />
              </Grid.Item>
            </Grid>
          </Grid.Item>
          <Grid.Item spanFromL={6}>
            <InputFloating
              name="attendingDoctorTelephone"
              label="Phone number"
              handleSave={handleSave}
            />
          </Grid.Item>
          <StyledSpacer spanFromL={6} />
          <Grid.Item spanFromL={6}>
            <InputFloating
              name="attendingDoctorEmail"
              label="Email"
              handleSave={handleSave}
            />
          </Grid.Item>
        </Grid>
      </Grid.Item>
      <Grid.Item>
        <Divider />
      </Grid.Item>
      <Pacemaker values={values} handleSave={handleSave} />
    </Grid>
  )
}

export default DoctorsAndMedical
